import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ProfileContext } from "../../../context/ProfileProvider";
import { SocketContext } from "../../../customComponents/SocketContext";
import { deskHasLivePrice, planOtcClientSockets } from "./otcSocketPolicy";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import AuthService from "../../../api/services/AuthService";
import OtcService from "../../../api/services/OtcService";
import { newIdempotencyKey } from "../../../api/services/fiatMoney";
import { fetchSpotBalance } from "../../../api/services/fiatService";
import { isKycVerified, kycTradeCtaLabel } from "../../../utils/kycVerified";
import { openKycVerification } from "../KycVara/openKycVerification";
import BigNumber from "bignumber.js";

const BACKUP_POLL_MS = 15000;
const AMOUNT_RE = /^\d+(\.\d+)?$/;

function readAuthToken() {
  try {
    return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
  } catch {
    return "";
  }
}

function coinCode(value) {
  if (value && typeof value === "object") {
    return String(value.short_name || value.code || value.symbol || value.name || "")
      .trim()
      .toUpperCase();
  }
  return String(value || "").trim().toUpperCase();
}

function positiveLast(...candidates) {
  for (const raw of candidates) {
    if (raw == null || raw === "") continue;
    const n = Number(String(raw).replace(/,/g, ""));
    if (Number.isFinite(n) && n > 0) return String(n);
  }
  return null;
}

function pairCodes(pair) {
  if (!pair) return { base: "", quote: "" };
  return {
    base: coinCode(pair.base_currency || pair.base_asset || pair.base),
    quote: coinCode(pair.quote_currency || pair.quote_asset || pair.quote),
  };
}

function matchSpotPair(pairs, payCoin, getCoin) {
  const a = coinCode(payCoin);
  const b = coinCode(getCoin);
  if (!a || !b) return null;
  const list = Array.isArray(pairs) ? pairs : [];
  return (
    list.find((p) => {
      const { base, quote } = pairCodes(p);
      return (base === a && quote === b) || (base === b && quote === a);
    }) || null
  );
}

function tickerFromSpotPair(pair) {
  if (!pair) return null;
  const { base, quote } = pairCodes(pair);
  const basePrice = positiveLast(pair.buy_price, pair.last, pair.close, pair.price);
  const quotePrice = positiveLast(pair.sell_price, pair.last, pair.close, pair.price);
  const last = basePrice || quotePrice;
  if (!last) return null;
  const change = pair.change_percentage ?? pair.change ?? pair.change_24hour;
  return {
    pair: `${base}/${quote}`,
    last,
    base_price: basePrice || last,
    quote_price: quotePrice || last,
    buy_price: basePrice || last,
    sell_price: quotePrice || last,
    change_percent: String(change ?? "0"),
  };
}

/** Buy spends quote, so the cost uses the base (buy) price. Sell spends base, so the proceeds use the quote (sell) price. */
function priceForSide(ticker, quoteType) {
  if (!ticker) return null;
  const selling = String(quoteType || "").toLowerCase() === "sell";
  if (selling) return positiveLast(ticker.quote_price, ticker.sell_price, ticker.last);
  return positiveLast(ticker.base_price, ticker.buy_price, ticker.last);
}

function apiMessage(res, fallback) {
  if (res && res.message) return res.message;
  return fallback;
}

function mapActivePairs(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      base: coinCode(row.base_currency || row.base),
      quote: coinCode(row.quote_currency || row.quote),
      name: String(row.base_currency_fullname || row.name || "").trim(),
      iconPath: String(row.icon_path || "").trim(),
    }))
    .filter((row) => row.base && row.quote && row.base !== row.quote);
}

function mapOtcPairs(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      base: coinCode(row.base),
      quote: coinCode(row.quote),
      name: String(row.name || "").trim(),
      buy_available: row.buy_available,
      sell_available: row.sell_available,
      min_base: String(row.min_base || "").trim(),
      max_base: String(row.max_base || "").trim(),
      min_notional: String(row.min_notional || "").trim(),
      max_notional: String(row.max_notional || "").trim(),
    }))
    .filter((row) => row.base && row.quote);
}

function otcListedPairs(activeRows, otcRows) {
  const spot = new Map((activeRows || []).map((row) => [pairKeyOf(row), row]));
  return (otcRows || []).map((row) => {
    const extra = spot.get(pairKeyOf(row));
    if (!extra) return row;
    return {
      ...row,
      name: row.name || extra.name,
      iconPath: row.iconPath || extra.iconPath,
    };
  });
}

function pickDefaultPair(pairs) {
  const list = Array.isArray(pairs) ? pairs : [];
  const btc = list.find((row) => coinCode(row.base) === "BTC" && coinCode(row.quote) === "USDT");
  const chosen = btc || list[0];
  if (!chosen || !chosen.base || !chosen.quote) return "BTC/USDT";
  return `${coinCode(chosen.base)}/${coinCode(chosen.quote)}`;
}

function pairKeyOf(row) {
  if (!row) return "";
  return `${coinCode(row.base)}/${coinCode(row.quote)}`;
}

function findListedPair(pairs, key) {
  const want = String(key || "").toUpperCase();
  return (Array.isArray(pairs) ? pairs : []).find((row) => pairKeyOf(row) === want) || null;
}

function formatMoney(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return String(raw || "");
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatApprox(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return "";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 8,
    maximumFractionDigits: 8,
  });
}

function trimDecimal(raw, places = 8) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return "";
  const scale = places === 2 ? 2 : 8;
  return n.toFixed(scale).replace(/\.?0+$/, "");
}

/** Never rounds up, so a max fill cannot size a payment above the available balance. */
function floorDecimal(raw, places) {
  const n = Number(raw);
  const scale = places === 2 ? 2 : 8;
  if (!Number.isFinite(n) || n <= 0) return "";
  const factor = 10 ** scale;
  const units = Math.floor(n * factor + 1e-8);
  if (units <= 0) return "";
  const whole = Math.floor(units / factor);
  const frac = String(units % factor).padStart(scale, "0").replace(/0+$/, "");
  return frac ? `${whole}.${frac}` : String(whole);
}

function stepDownDecimal(text, places) {
  const scale = places === 2 ? 2 : 8;
  const factor = 10 ** scale;
  const units = Math.round(Number(text) * factor) - 1;
  if (!Number.isFinite(units) || units <= 0) return "";
  const whole = Math.floor(units / factor);
  const frac = String(units % factor).padStart(scale, "0").replace(/0+$/, "");
  return frac ? `${whole}.${frac}` : String(whole);
}

/**
 * Largest amount in the coin the user is typing whose sized payment is still
 * within the available balance. Manual amounts are not changed here.
 */
function maxAffordableAmount({ quoteType, spendBalance, amountAsset, baseCode, quoteCode, last }) {
  const have = Number(spendBalance);
  const lastText = positiveLast(last);
  if (!Number.isFinite(have) || have <= 0 || !lastText || !amountAsset || !baseCode || !quoteCode) return "";
  const price = Number(lastText);
  const enteredInQuote = amountAsset === quoteCode;
  const typingSpendCoin = quoteType === "sell" ? amountAsset === baseCode : amountAsset === quoteCode;
  const places = enteredInQuote ? quotePlaces(quoteCode) : 8;
  let raw = have;
  if (!typingSpendCoin) {
    if (!(price > 0)) return "";
    raw = enteredInQuote ? have * price : have / price;
  }
  let candidate = floorDecimal(raw, places);
  for (let i = 0; i < 6 && candidate; i += 1) {
    const sized = sizeRfq({
      quoteType,
      amount: candidate,
      amountAsset,
      baseCode,
      quoteCode,
      last: lastText,
    });
    if (sized && Number(sized.pay) <= have) return candidate;
    candidate = stepDownDecimal(candidate, places);
  }
  return "";
}

function quotePlaces(code) {
  const asset = String(code || "").toUpperCase();
  if (asset === "USDT" || asset === "USDC" || asset === "USD") return 2;
  return 8;
}

/** Digits and one decimal point. Extra dots and letters are dropped. */
function sanitizeAmount(raw, places = 8) {
  let text = String(raw ?? "").replace(/[^\d.]/g, "");
  const firstDot = text.indexOf(".");
  if (firstDot >= 0) {
    text = text.slice(0, firstDot + 1) + text.slice(firstDot + 1).replace(/\./g, "");
  }
  const parts = text.split(".");
  const whole = (parts[0] || "").replace(/^0+(?=\d)/, "");
  if (parts.length === 1) return whole;
  const scale = places === 2 ? 2 : 8;
  return `${whole || "0"}.${parts[1].slice(0, scale)}`;
}

function decimalLt(left, right) {
  const a = new BigNumber(String(left || "").trim());
  const b = new BigNumber(String(right || "").trim());
  if (!a.isFinite() || !b.isFinite()) return false;
  return a.lt(b);
}

function minimumAmountError({ notional, minNotional, baseAmount, minBase, quoteCode, baseCode }) {
  const minValue = String(minNotional || "").trim();
  const minCoin = String(minBase || "").trim();
  if (minValue && notional && decimalLt(notional, minValue)) {
    return `Minimum order value is ${formatMoney(minValue)} ${quoteCode}.`;
  }
  if (minCoin && baseAmount && decimalLt(baseAmount, minCoin)) {
    return `Minimum amount is ${minCoin} ${baseCode}.`;
  }
  return "";
}

function quoteBelowMinimum(row, pairs, globalMin, minBaseByAsset) {
  if (!row) return "";
  const base = coinCode(row.pair_base);
  const quoteCode = coinCode(row.pair_quote);
  const listed = findListedPair(pairs, base && quoteCode ? `${base}/${quoteCode}` : "");
  const userLimit = listed && listed.limit_source === "user";
  const pay = coinCode(row.pay_asset);
  const get = coinCode(row.get_asset);
  const notional = pay === quoteCode ? row.pay_amount : get === quoteCode ? row.get_amount : "";
  const baseAmount = pay === base ? row.pay_amount : get === base ? row.get_amount : "";
  return minimumAmountError({
    notional,
    minNotional: (listed && listed.min_notional) || (userLimit ? "" : globalMin) || "",
    baseAmount,
    minBase: (listed && listed.min_base) || (userLimit ? "" : (minBaseByAsset && minBaseByAsset[base])) || "",
    quoteCode,
    baseCode: base,
  });
}

/**
 * One limit, in the coin the user typed.
 * Quote entry uses the USDT order value. Base entry uses that value converted at the live price.
 * The higher minimum wins. The lower maximum wins. The other coin is not checked again.
 */
function amountUnitLimit({ enteredInQuote, baseRaw, quoteRaw, price, quoteCode, pick }) {
  const baseN = Number(baseRaw);
  const quoteN = Number(quoteRaw);
  const hasBase = Number.isFinite(baseN) && baseN > 0;
  const hasQuote = Number.isFinite(quoteN) && quoteN > 0;
  const px = Number(price);
  const parts = [];
  if (enteredInQuote) {
    if (hasQuote) parts.push(quoteN);
    if (hasBase && px > 0) parts.push(baseN * px);
  } else {
    if (hasBase) parts.push(baseN);
    if (hasQuote && px > 0) parts.push(quoteN / px);
  }
  if (!parts.length) return "";
  const n = pick === "max" ? Math.min(...parts) : Math.max(...parts);
  if (!Number.isFinite(n) || n <= 0) return "";
  return trimDecimal(n, enteredInQuote ? quotePlaces(quoteCode) : 8);
}

/** Buy pays the quote coin and receives the base coin. Sell is the reverse. The amount coin is only the unit typed. */
function sizeRfq({ quoteType, amount, amountAsset, baseCode, quoteCode, last }) {
  const qty = String(amount || "").trim();
  const lastText = positiveLast(last);
  if (!lastText || !baseCode || !quoteCode) return null;
  if (!AMOUNT_RE.test(qty) || qty === "0" || /^0+(\.0+)?$/.test(qty)) return null;
  const enteredInQuote = amountAsset === quoteCode;
  const price = Number(lastText);
  const baseQty = enteredInQuote ? trimDecimal(Number(qty) / price) : qty;
  const qtyN = Number(baseQty);
  if (!Number.isFinite(qtyN) || qtyN <= 0) return null;
  // A quote amount typed by the user is the order value. Rebuilding it from the
  // truncated base quantity dips under an exact minimum (200000 → 199999.999…).
  const notionalN = enteredInQuote ? Number(qty) : qtyN * price;
  // A typed quote amount stays as entered. A derived quote payment is floored so
  // the balance check cannot round a buy above the wallet.
  const quoteNotional = enteredInQuote
    ? trimDecimal(notionalN, quotePlaces(quoteCode))
    : floorDecimal(notionalN, quotePlaces(quoteCode));
  const pay = quoteType === "sell" ? baseQty : quoteNotional;
  const get = quoteType === "sell" ? quoteNotional : baseQty;
  if (!AMOUNT_RE.test(String(pay)) || !AMOUNT_RE.test(String(get))) return null;
  return {
    pay,
    get,
    payAsset: quoteType === "sell" ? baseCode : quoteCode,
    getAsset: quoteType === "sell" ? quoteCode : baseCode,
    baseQty,
    quoteNotional,
    qtyN,
    notionalN,
    enteredInQuote,
  };
}

function reviewAmount({
  amount,
  last,
  sized,
  baseCode,
  quoteCode,
  payCoin,
  minQtyRaw,
  pairMin,
  pairMax,
  pairMaxCoin,
  spendBalance,
  enteredInQuote,
}) {
  const empty = { error: "", warning: "" };
  const text = String(amount || "").trim();
  if (!text) return empty;
  if (!AMOUNT_RE.test(text)) return { error: "Enter a valid amount.", warning: "" };
  if (text === "0" || /^0+(\.0+)?$/.test(text)) return { error: "Enter an amount greater than 0.", warning: "" };
  if (!last) {
    const early = enteredInQuote
      ? minimumAmountError({
        notional: text,
        minNotional: pairMin,
        quoteCode,
        baseCode,
      })
      : "";
    return { error: early || "Live price is not available yet.", warning: "" };
  }
  if (!sized) return { error: "Enter a valid amount.", warning: "" };
  const belowMin = minimumAmountError({
    notional: sized.quoteNotional,
    minNotional: pairMin,
    baseAmount: sized.baseQty,
    minBase: minQtyRaw,
    quoteCode,
    baseCode,
  });
  if (belowMin) return { error: belowMin, warning: "" };
  const price = Number(positiveLast(last) || 0);
  const unit = sized.enteredInQuote ? quoteCode : baseCode;
  const limitArgs = { enteredInQuote: sized.enteredInQuote, price, quoteCode };
  const coinMaxText = amountUnitLimit({
    ...limitArgs,
    baseRaw: pairMaxCoin,
    quoteRaw: "",
    pick: "max",
  });
  // Quote ceiling converted with the live side price, in the coin the user typed.
  const dynamicMaxText = amountUnitLimit({
    ...limitArgs,
    baseRaw: "",
    quoteRaw: pairMax,
    pick: "max",
  });
  const typed = Number(text);
  const aboveDeskMax = Boolean(dynamicMaxText && typed > Number(dynamicMaxText));
  if (!aboveDeskMax && coinMaxText && typed > Number(coinMaxText)) {
    return {
      error: `Maximum amount is ${sized.enteredInQuote ? formatMoney(coinMaxText) : coinMaxText} ${unit}.`,
      warning: "",
    };
  }
  const payN = Number(sized.pay);
  const haveN = Number(spendBalance);
  if (spendBalance !== "" && Number.isFinite(payN) && Number.isFinite(haveN) && payN > haveN) {
    return {
      error: `Insufficient ${String(payCoin || sized.payAsset || "").toUpperCase()} balance (needed ${sized.pay}, available ${spendBalance}).`,
      warning: "",
    };
  }
  if (!aboveDeskMax) return empty;
  const shownMax = sized.enteredInQuote ? formatMoney(dynamicMaxText) : dynamicMaxText;
  return {
    error: "",
    warning: `Above the desk maximum of ${shownMax} ${unit}. This request goes to the desk. Compliance reviews it after you accept a quote.`,
  };
}

function remainingMs(expiresAt, nowMs) {
  if (!expiresAt) return 0;
  const end = new Date(expiresAt).getTime();
  if (Number.isNaN(end)) return 0;
  return Math.max(0, end - nowMs);
}

function rfqMatchesPair(row, pairKey) {
  const pay = String(row?.pay_asset || "").toUpperCase();
  const get = String(row?.get_asset || "").toUpperCase();
  const [base = "", quote = ""] = String(pairKey || "").toUpperCase().split("/");
  if (!pay || !get || !base || !quote) return false;
  return (pay === base && get === quote) || (pay === quote && get === base);
}

function rfqPairKey(row) {
  const pay = String(row?.pay_asset || "").toUpperCase();
  const get = String(row?.get_asset || "").toUpperCase();
  const side = String(row?.side || "").toUpperCase();
  if (!pay || !get) return "";
  if (side === "SELL") return `${pay}/${get}`;
  return `${get}/${pay}`;
}

function deskView(rfq, quote, nowMs) {
  if (!rfq) return "form";
  if (rfq.status === "CANCELLED") return "cancelled";
  if (rfq.status === "ACCEPTED" || quote?.status === "ACCEPTED") return "accepted";
  if (quote?.status === "VOID") return "void";
  const requestClosed = rfq.status === "OPEN" && rfq.expiresAt && remainingMs(rfq.expiresAt, nowMs) <= 0;
  const expired =
    rfq.status === "EXPIRED" ||
    rfq.status === "REJECTED_TIMEOUT" ||
    quote?.status === "EXPIRED" ||
    requestClosed ||
    (quote?.status === "ISSUED" && remainingMs(quote.expiresAt, nowMs) <= 0);
  if (expired) return "expired";
  if (rfq.status === "OPEN") return "awaiting";
  if (rfq.status === "QUOTED" && quote?.status === "ISSUED") return "quote";
  if (rfq.status === "QUOTED") return "awaiting";
  return "form";
}

export default function useOtcDesk() {
  const location = useLocation();
  const navigate = useNavigate();
  const { userDetails, profileHydrated } = useContext(ProfileContext) || {};
  const {
    socket,
    isConnected,
    marketData,
    subscribeToMarket,
    unsubscribeFromMarket,
  } = useContext(SocketContext) || {};
  const kycCtaLabel = kycTradeCtaLabel(userDetails?.kycVerified);

  const [isLoggedIn, setIsLoggedIn] = useState(() => Boolean(readAuthToken()));
  const kycKnown = !isLoggedIn || profileHydrated === true;
  const kycVerified = kycKnown && isKycVerified(userDetails?.kycVerified);
  const kycPending = isLoggedIn && !kycKnown;
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const draft = location.state && location.state.otcDraft;
  const passedPairRef = useRef(draft && draft.pairKey ? String(draft.pairKey).toUpperCase() : "");
  const [quoteType, setQuoteType] = useState(draft && draft.quoteType === "sell" ? "sell" : "buy");
  const [pairKey, setPairKey] = useState(draft && draft.pairKey ? String(draft.pairKey).toUpperCase() : "BTC/USDT");
  const [amountCoin, setAmountCoinState] = useState(draft ? coinCode(draft.amountAsset) : "");
  const [amount, setAmount] = useState(draft && draft.amount != null ? String(draft.amount) : "");
  const maxLockRef = useRef(false);
  const maxScopeRef = useRef("");
  const [minNotional, setMinNotional] = useState("");
  const [maxNotional, setMaxNotional] = useState("");
  const [minBaseByAsset, setMinBaseByAsset] = useState({});
  const [available, setAvailable] = useState("");
  const [spendBalance, setSpendBalance] = useState("");
  const [lockedBalance, setLockedBalance] = useState("");
  const [getBalance, setGetBalance] = useState("");
  const [activePairs, setActivePairs] = useState([]);
  const [otcPairs, setOtcPairs] = useState([]);
  const [deskOpen, setDeskOpen] = useState(true);
  const [rfq, setRfq] = useState(() => location.state?.rfq || null);
  const [liveByPair, setLiveByPair] = useState({});
  const liveByPairRef = useRef({});
  const pairKeyRef = useRef(pairKey);

  useEffect(() => {
    if (location.state?.rfq) setRfq(location.state.rfq);
  }, [location.state]);
  const [quote, setQuote] = useState(null);
  const rfqRef = useRef(rfq);
  const quoteRef = useRef(quote);
  const notifiedQuoteId = useRef("");
  const dismissedRfqId = useRef("");
  const acceptKeyRef = useRef({ quoteId: "", key: "" });
  const submitLockRef = useRef(false);
  rfqRef.current = rfq;
  quoteRef.current = quote;
  liveByPairRef.current = liveByPair;
  pairKeyRef.current = pairKey;
  const [trade, setTrade] = useState(null);
  const [trades, setTrades] = useState([]);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [ticker, setTicker] = useState(null);

  useEffect(() => {
    const syncAuth = () => setIsLoggedIn(Boolean(readAuthToken()));
    window.addEventListener("agce-auth-changed", syncAuth);
    window.addEventListener("agce-auth-token-changed", syncAuth);
    window.addEventListener("storage", syncAuth);
    return () => {
      window.removeEventListener("agce-auth-changed", syncAuth);
      window.removeEventListener("agce-auth-token-changed", syncAuth);
      window.removeEventListener("storage", syncAuth);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadActivePairs() {
      setAssetsLoading(true);
      const res = await AuthService.getPairs();
      if (cancelled) return;
      setAssetsLoading(false);
      if (!res?.success) return;
      setActivePairs(mapActivePairs(res.data));
    }
    loadActivePairs();
    return () => {
      cancelled = true;
    };
  }, []);

  const pairs = useMemo(
    () => otcListedPairs(activePairs, otcPairs),
    [activePairs, otcPairs]
  );

  useEffect(() => {
    if (!pairs.length) return;
    setPairKey((prev) => {
      const passed = passedPairRef.current;
      if (passed && findListedPair(pairs, passed)) return passed;
      if (findListedPair(pairs, prev)) return prev;
      return pickDefaultPair(pairs);
    });
  }, [pairs]);

  useEffect(() => {
    let cancelled = false;
    async function loadAssets() {
      setAssetsLoading(true);
      const res = await OtcService.getAssets();
      if (cancelled) return;
      setAssetsLoading(false);
      if (!res?.success) return;
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setAssets(items);
      setOtcPairs(mapOtcPairs(res.data?.pairs));
      setDeskOpen(res.data?.desk_open !== false);
      setMinNotional(res.data?.min_notional != null ? String(res.data.min_notional) : "");
      setMaxNotional(res.data?.max_notional != null ? String(res.data.max_notional) : "");
      setMinBaseByAsset(
        res.data?.min_base && typeof res.data.min_base === "object" ? res.data.min_base : {}
      );
    }
    loadAssets();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!isLoggedIn) {
      setTrades([]);
      return undefined;
    }

    async function loadTrades() {
      setTradesLoading(true);
      const res = await OtcService.getTrades({ page: 1, limit: 20 });
      if (cancelled) return;
      setTradesLoading(false);
      const items = res?.success ? res.data?.items : null;
      if (!Array.isArray(items)) return;
      setTrades(items);
    }

    async function loadCurrent() {
      const res = await OtcService.getCurrent();
      if (cancelled || !res?.success || !res.data?.rfq?.id) return;
      setRfq((prev) => (prev?.id ? prev : res.data.rfq));
      if (res.data.quote?.id) {
        setQuote((prev) => (prev?.id ? prev : res.data.quote));
      }
    }

    loadTrades();
    loadCurrent();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  const applyIncomingQuote = useCallback((nextRfq, nextQuote) => {
    if (!nextRfq?.id || nextRfq.id === dismissedRfqId.current) return;
    const key = rfqPairKey(nextRfq);
    if (key) {
      setLiveByPair((prev) => ({
        ...prev,
        [key]: { rfq: nextRfq, quote: nextQuote?.id ? nextQuote : (prev[key]?.quote || null) },
      }));
    }
    const current = rfqRef.current;
    const onThisPair = Boolean(key) && key === String(pairKeyRef.current || "").toUpperCase();
    if (!onThisPair && (!current?.id || current.id !== nextRfq.id)) return;
    const wasWaiting = String(current?.status || "").toUpperCase() === "OPEN" && current.id === nextRfq.id;
    setRfq(nextRfq);
    if (!nextQuote?.id) return;
    setQuote(nextQuote);
    if (
      wasWaiting
      && nextQuote.status === "ISSUED"
      && notifiedQuoteId.current !== nextQuote.id
    ) {
      notifiedQuoteId.current = nextQuote.id;
      alertSuccessMessage("Quote is ready. You can accept it.");
    }
  }, []);

  useEffect(() => {
    if (!socket || typeof socket.on !== "function") return undefined;
    const onQuote = (payload) => {
      applyIncomingQuote(payload && payload.rfq, payload && payload.quote);
    };
    const onPairs = (payload) => {
      const rows = payload && Array.isArray(payload.pairs) ? payload.pairs : null;
      if (!rows) return;
      setOtcPairs(mapOtcPairs(rows));
      if (payload.min_notional != null) setMinNotional(String(payload.min_notional));
      if (payload.max_notional != null) setMaxNotional(String(payload.max_notional));
    };
    socket.on("otc:quote", onQuote);
    socket.on("otc:pairs", onPairs);
    return () => {
      socket.off("otc:quote", onQuote);
      socket.off("otc:pairs", onPairs);
    };
  }, [socket, applyIncomingQuote]);

  useEffect(() => {
    if (!isLoggedIn || !isConnected) return undefined;
    if (String(rfqRef.current?.status || "").toUpperCase() !== "OPEN") return undefined;
    let stopped = false;
    OtcService.getCurrent().then((res) => {
      if (stopped || !res?.success || !res.data?.rfq?.id) return;
      applyIncomingQuote(res.data.rfq, res.data.quote);
    });
    return () => {
      stopped = true;
    };
  }, [isLoggedIn, isConnected, applyIncomingQuote]);

  useEffect(() => {
    if (!isLoggedIn || isConnected) return undefined;
    let stopped = false;
    async function tick() {
      const status = String(rfqRef.current?.status || "").toUpperCase();
      const quoteStatus = String(quoteRef.current?.status || "").toUpperCase();
      const waiting = !rfqRef.current?.id
        || status === "OPEN"
        || (status === "QUOTED" && quoteStatus !== "ISSUED");
      if (!waiting) return;
      const res = await OtcService.getCurrent();
      if (stopped || !res?.success || !res.data?.rfq?.id) return;
      applyIncomingQuote(res.data.rfq, res.data.quote);
    }
    const interval = setInterval(tick, BACKUP_POLL_MS);
    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [isLoggedIn, isConnected, applyIncomingQuote]);

  const expiredSyncRef = useRef("");

  const refreshTradeList = useCallback(async () => {
    const tradesRes = await OtcService.getTrades({ page: 1, limit: 20 });
    const items = tradesRes?.success ? tradesRes.data?.items : null;
    if (Array.isArray(items)) setTrades(items);
  }, []);

  const applyExpiredRfq = useCallback(async (id) => {
    const res = await OtcService.getRfq(id);
    if (res?.success && res.data?.id) setRfq(res.data);
    else setRfq((prev) => (prev && String(prev.id) === String(id) ? { ...prev, status: "EXPIRED" } : prev));
    await refreshTradeList();
  }, [refreshTradeList]);

  const applyExpiredQuote = useCallback(async () => {
    const res = await OtcService.getCurrent();
    const next = res?.success ? res.data?.rfq : null;
    const nextStatus = String(next?.status || "").toUpperCase();
    if (next?.id && (nextStatus === "OPEN" || nextStatus === "QUOTED")) {
      setRfq(next);
      if (res.data?.quote?.id) setQuote(res.data.quote);
      else setQuote((prev) => (prev ? { ...prev, status: "EXPIRED" } : prev));
    } else {
      setRfq((prev) => (prev ? { ...prev, status: "REJECTED_TIMEOUT" } : prev));
      setQuote((prev) => (prev ? { ...prev, status: "EXPIRED" } : prev));
    }
    await refreshTradeList();
  }, [refreshTradeList]);

  useEffect(() => {
    const requestOpen = String(rfq?.status || "").toUpperCase() === "OPEN" && rfq?.expiresAt;
    const quoteLive = quote?.status === "ISSUED" && quote?.expiresAt;
    if (!requestOpen && !quoteLive) return undefined;
    const tick = () => {
      const now = Date.now();
      setNowMs(now);
      if (!isLoggedIn) return;
      if (requestOpen && rfq?.id && remainingMs(rfq.expiresAt, now) <= 0) {
        if (expiredSyncRef.current !== String(rfq.id)) {
          expiredSyncRef.current = String(rfq.id);
          applyExpiredRfq(rfq.id);
        }
        return;
      }
      if (quoteLive && quote?.id && remainingMs(quote.expiresAt, now) <= 0) {
        const key = `quote:${quote.id}`;
        if (expiredSyncRef.current === key) return;
        expiredSyncRef.current = key;
        applyExpiredQuote();
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [quote?.id, quote?.status, quote?.expiresAt, rfq?.id, rfq?.status, rfq?.expiresAt, isLoggedIn, applyExpiredRfq, applyExpiredQuote]);

  const view = deskView(rfq, quote, nowMs);
  const selectedPairKey = String(pairKey || "").toUpperCase();
  const liveRequests = useMemo(() => {
    const seen = new Set();
    const list = [];
    const push = (row, nextQuote) => {
      if (!row?.id || seen.has(String(row.id))) return;
      const open = deskView(row, nextQuote, nowMs);
      if (open !== "awaiting" && open !== "quote") return;
      seen.add(String(row.id));
      list.push({ rfq: row, quote: nextQuote || null });
    };
    Object.values(liveByPair).forEach((row) => push(row?.rfq, row?.quote));
    push(rfq, quote);
    return list;
  }, [liveByPair, rfq, quote, nowMs]);
  const blockingTicket = liveRequests[0] || null;
  const blockingPair = blockingTicket
    ? (rfqPairKey(blockingTicket.rfq) || selectedPairKey)
    : selectedPairKey;
  const blockingView = blockingTicket
    ? deskView(blockingTicket.rfq, blockingTicket.quote, nowMs)
    : "form";
  const requestWindowOpen = Boolean(blockingTicket);
  const requestWindowMessage = !requestWindowOpen
    ? ""
    : blockingView === "quote"
      ? `A firm ${blockingPair} quote is still open. Accept it or wait until it expires before sending another request.`
      : `You already have a ${blockingPair} quote request open. Cancel it or wait until it closes before sending another.`;

  useEffect(() => {
    if (!rfq?.id) return;
    const key = rfqPairKey(rfq);
    if (!key) return;
    const open = view === "awaiting" || view === "quote";
    setLiveByPair((prev) => {
      if (!open) {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      if (prev[key]?.rfq === rfq && prev[key]?.quote === quote) return prev;
      return { ...prev, [key]: { rfq, quote: quote || null } };
    });
  }, [rfq, quote, view]);

  useEffect(() => {
    const key = String(pairKey || "").toUpperCase();
    const saved = liveByPairRef.current[key];
    const open = saved ? deskView(saved.rfq, saved.quote, Date.now()) : "form";
    if (saved?.rfq && (open === "awaiting" || open === "quote")) {
      if (String(rfqRef.current?.id || "") !== String(saved.rfq.id)) setRfq(saved.rfq);
      setQuote(saved.quote && saved.quote.id ? saved.quote : null);
      return;
    }
    if (rfqRef.current && !rfqMatchesPair(rfqRef.current, key)) {
      setRfq(null);
      setQuote(null);
    }
  }, [pairKey]);
  const countdownMs = quote ? remainingMs(quote.expiresAt, nowMs) : 0;
  const requestLeftMs = rfq?.expiresAt ? remainingMs(rfq.expiresAt, nowMs) : 0;
  const selectedPair = useMemo(() => findListedPair(pairs, pairKey), [pairs, pairKey]);
  const payCoin = quoteType === "sell" ? coinCode(selectedPair?.base) : coinCode(selectedPair?.quote);
  const getCoin = quoteType === "sell" ? coinCode(selectedPair?.quote) : coinCode(selectedPair?.base);
  const pairBase = coinCode(selectedPair?.base);
  const pairQuote = coinCode(selectedPair?.quote);
  const amountAsset = amountCoin === pairBase || amountCoin === pairQuote
    ? amountCoin
    : (pairBase || pairQuote);
  const spendAsset = quoteType === "sell" ? pairBase : pairQuote;

  useEffect(() => {
    if (!pairBase && !pairQuote) return;
    setAmountCoinState((prev) => (prev === pairBase || prev === pairQuote ? prev : pairBase || pairQuote));
  }, [pairBase, pairQuote]);

  const setAmountCoin = useCallback((code) => {
    const next = coinCode(code);
    if (next !== pairBase && next !== pairQuote || next === amountCoin) return;
    setAmountCoinState(next);
    maxLockRef.current = false;
    setAmount("");
  }, [pairBase, pairQuote, amountCoin]);
  const balanceGen = useRef(0);

  const refreshSpotBalances = useCallback(async () => {
    const gen = balanceGen.current + 1;
    balanceGen.current = gen;
    if (!isLoggedIn) {
      setAvailable("");
      setSpendBalance("");
      setLockedBalance("");
      setGetBalance("");
      return;
    }
    const payCode = spendAsset;
    if (!payCode && !getCoin) return;
    const pay = payCode ? await fetchSpotBalance(payCode) : null;
    if (balanceGen.current !== gen) return;
    const payBalance = pay && pay.ok ? String(pay.balance || "0") : "0";
    const payLocked = pay && pay.ok ? String(pay.locked || "0") : "0";
    setAvailable(payBalance);
    setSpendBalance(payBalance);
    setLockedBalance(payLocked);
    if (getCoin && getCoin !== payCode) {
      const received = await fetchSpotBalance(getCoin);
      if (balanceGen.current !== gen) return;
      setGetBalance(received && received.ok ? String(received.balance || "0") : "0");
      return;
    }
    setGetBalance(payBalance);
  }, [isLoggedIn, spendAsset, getCoin]);

  const matchedSpotPair = useMemo(
    () => matchSpotPair(marketData?.pairs, payCoin, getCoin),
    [marketData?.pairs, payCoin, getCoin]
  );

  const socketPlan = planOtcClientSockets({
    hasPair: Boolean(payCoin && getCoin),
  });

  useEffect(() => {
    if (!socketPlan.market) return undefined;
    if (typeof subscribeToMarket === "function") subscribeToMarket();
    return () => {
      if (typeof unsubscribeFromMarket === "function") unsubscribeFromMarket();
    };
  }, [socketPlan.market, isConnected, subscribeToMarket, unsubscribeFromMarket]);

  useEffect(() => {
    if (!payCoin || !getCoin) {
      setTicker(null);
      return undefined;
    }

    const live = tickerFromSpotPair(matchedSpotPair);
    if (live) {
      setTicker(live);
      return undefined;
    }

    // This pair is not in the spot snapshot. Drop the previous pair's price
    // so an RFQ cannot be sized from a stale last.
    setTicker(null);
    if (isConnected) return undefined;

    let stopped = false;
    let timer;
    async function loadBackup() {
      const res = await OtcService.getTicker({
        pay_asset: String(payCoin).toUpperCase(),
        get_asset: String(getCoin).toUpperCase(),
      });
      if (stopped) return;
      const last = res?.success ? positiveLast(res.data?.last, res.data?.buy_price, res.data?.sell_price) : null;
      if (last) {
        const basePrice = positiveLast(res.data?.buy_price, res.data?.base_price, last);
        const quotePrice = positiveLast(res.data?.sell_price, res.data?.quote_price, last);
        setTicker({
          ...res.data,
          pair: `${String(payCoin).toUpperCase()}/${String(getCoin).toUpperCase()}`,
          last,
          base_price: basePrice,
          quote_price: quotePrice,
          buy_price: basePrice,
          sell_price: quotePrice,
        });
      }
      timer = setTimeout(loadBackup, BACKUP_POLL_MS);
    }

    loadBackup();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [payCoin, getCoin, matchedSpotPair, isConnected]);

  const buyOpen = true;
  const sellOpen = true;

  const landingBalanceOk = useCallback(() => {
    const last = priceForSide(ticker, quoteType);
    const baseCode = coinCode(selectedPair && selectedPair.base);
    const quoteCode = coinCode(selectedPair && selectedPair.quote);
    const sized = sizeRfq({
      quoteType,
      amount,
      amountAsset,
      baseCode,
      quoteCode,
      last,
    });
    if (!sized) return true;
    const payN = Number(sized.pay);
    const haveN = Number(spendBalance);
    if (spendBalance === "") {
      alertErrorMessage("Checking your available balance. Try again in a moment.");
      return false;
    }
    if (Number.isFinite(payN) && Number.isFinite(haveN) && payN > haveN) {
      alertErrorMessage(
        `Insufficient ${String(sized.payAsset || payCoin).toUpperCase()} balance (needed ${sized.pay}, available ${spendBalance})`
      );
      return false;
    }
    return true;
  }, [ticker, selectedPair, quoteType, amount, amountAsset, spendBalance, payCoin]);

  const submitRfq = useCallback(async () => {
    if (!/\/rfq\/?$/.test(location.pathname)) return;
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    try {
    if (!isLoggedIn) {
      navigate("/login", { state: { redirectTo: "/otc/rfq" } });
      return;
    }
    if (requestWindowOpen) {
      alertErrorMessage(requestWindowMessage);
      return;
    }
    if (kycPending) {
      alertErrorMessage("Checking your account. Try again in a moment.");
      return;
    }
    if (isLoggedIn && !kycVerified) {
      alertErrorMessage("Submit your KYC before requesting a quote.");
      openKycVerification();
      return;
    }
    if (!selectedPair || !payCoin || !getCoin) {
      alertErrorMessage("Select a pair before requesting a quote.");
      return;
    }
    const last = deskHasLivePrice(ticker, payCoin, getCoin) ? priceForSide(ticker, quoteType) : null;
    const baseCode = coinCode(selectedPair.base);
    const quoteCode = coinCode(selectedPair.quote);
    const sized = sizeRfq({
      quoteType,
      amount,
      amountAsset,
      baseCode,
      quoteCode,
      last,
    });
    if (!last) {
      alertErrorMessage("Live last is not available yet.");
      return;
    }
    if (!sized) {
      alertErrorMessage("Please enter a valid amount.");
      return;
    }
    const { pay, get } = sized;
    const userLimit = selectedPair && selectedPair.limit_source === "user";
    const pairMinCoin = String(selectedPair.min_base || "").trim();
    const minQtyRaw = pairMinCoin || (userLimit ? "" : minBaseByAsset[baseCode]);
    const pairMin = String(
      (selectedPair && selectedPair.min_notional) || (userLimit ? "" : minNotional) || ""
    ).trim();
    const pairMax = String(
      (selectedPair && selectedPair.max_notional) || (userLimit ? "" : maxNotional) || ""
    ).trim();
    const pairMaxCoin = String(selectedPair.max_base || "").trim();
    const problem = reviewAmount({
      amount,
      last,
      sized,
      baseCode,
      quoteCode,
      payCoin,
      minQtyRaw,
      pairMin,
      pairMax,
      pairMaxCoin,
      spendBalance,
      enteredInQuote: amountAsset === quoteCode,
    }).error;
    if (problem) {
      alertErrorMessage(problem);
      return;
    }
    if (!deskOpen) {
      alertErrorMessage("The OTC desk is closed. Try again during desk hours.");
      return;
    }
    if (!AMOUNT_RE.test(pay) || !AMOUNT_RE.test(get)) {
      alertErrorMessage("Could not size the RFQ from the live last.");
      return;
    }
    setSubmitting(true);
    const res = await OtcService.createRfq({
      side: String(quoteType).toUpperCase(),
      pay_asset: String(payCoin).toUpperCase(),
      pay_amount: pay,
      get_asset: String(getCoin).toUpperCase(),
      get_amount: get,
      request_rate: last,
    });
    if (!res?.success) {
      if (res?.code === "KYC_REQUIRED" || res?.code === "KYB_REQUIRED") {
        alertErrorMessage(apiMessage(res, "Submit your KYC before requesting a quote."));
        openKycVerification();
        return;
      }
      if (res?.code === "RFQ_ALREADY_OPEN") {
        const current = await OtcService.getCurrent();
        const row = current?.success ? current.data?.rfq : null;
        if (row?.id) {
          const key = rfqPairKey(row);
          const issued = current.data?.quote?.id ? current.data.quote : null;
          if (key) {
            setLiveByPair((prev) => ({
              ...prev,
              [key]: { rfq: row, quote: issued || prev[key]?.quote || null },
            }));
          }
          if (!key || rfqMatchesPair(row, pairKeyRef.current)) {
            dismissedRfqId.current = "";
            setTrade(null);
            setRfq(row);
            setQuote(issued);
          }
        }
      }
      alertErrorMessage(apiMessage(res, "Could not create RFQ."));
      return;
    }
    const created = res.data || {};
    setTrade(null);
    setRfq(created);
    setQuote(created.quote && created.quote.id ? created.quote : null);
    maxLockRef.current = false;
    setAmount("");
    alertSuccessMessage(
      created.quote && created.quote.id
        ? "Firm quote ready. Review it and accept."
        : "Quote requested. Awaiting desk."
    );
    return res.data;
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  }, [location.pathname, isLoggedIn, kycPending, kycVerified, navigate, amount, amountAsset, ticker, payCoin, getCoin, quoteType, selectedPair, minBaseByAsset, minNotional, maxNotional, spendBalance, deskOpen, requestWindowOpen, requestWindowMessage]);

  const dropLiveRequest = useCallback((subject) => {
    const key = rfqPairKey(subject);
    setLiveByPair((prev) => {
      if (!key || !prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const cancelRfq = useCallback(async (target) => {
    const subject = target?.id ? target : rfq;
    if (!subject?.id) return false;
    const status = String(subject.status || "").toUpperCase();
    const closed = status === "EXPIRED" || (subject.expiresAt && remainingMs(subject.expiresAt, Date.now()) <= 0);
    const isCurrent = String(rfqRef.current?.id || "") === String(subject.id);
    if (status !== "OPEN" || closed) {
      if (closed) {
        if (isCurrent) await applyExpiredRfq(subject.id);
        else {
          dropLiveRequest(subject);
          await refreshTradeList();
        }
        alertErrorMessage("This request has expired.");
      }
      return false;
    }
    setSubmitting(true);
    const res = await OtcService.cancelRfq(subject.id);
    setSubmitting(false);
    if (!res?.success) {
      const message = apiMessage(res, "Could not cancel RFQ.");
      if (res?.code === "RFQ_NOT_CANCELLABLE" && /EXPIRED/i.test(message)) {
        if (isCurrent) await applyExpiredRfq(subject.id);
        else {
          dropLiveRequest(subject);
          await refreshTradeList();
        }
        alertErrorMessage("This request has expired.");
        return false;
      }
      alertErrorMessage(message);
      return false;
    }
    if (isCurrent) setRfq(res.data);
    else dropLiveRequest(subject);
    setTrades((prev) => (Array.isArray(prev) ? prev : []).map((row) => (
      String(row?.id) === String(subject.id) ? { ...row, ...(res.data || {}), status: "CANCELLED" } : row
    )));
    await refreshTradeList();
    alertSuccessMessage("RFQ cancelled.");
    return true;
  }, [rfq, applyExpiredRfq, dropLiveRequest, refreshTradeList]);

  const acceptLimitError = quoteBelowMinimum(quote, pairs, minNotional, minBaseByAsset);

  const acceptQuote = useCallback(async () => {
    const blocked = quoteBelowMinimum(quote, pairs, minNotional, minBaseByAsset);
    if (blocked) {
      alertErrorMessage(blocked);
      return;
    }
    if (kycPending || !quote?.id) return;
    if (!kycVerified) {
      alertErrorMessage("Submit your KYC before accepting a quote.");
      openKycVerification();
      return;
    }
    setSubmitting(true);
    const quoteId = String(quote.id);
    if (acceptKeyRef.current.quoteId !== quoteId || !acceptKeyRef.current.key) {
      acceptKeyRef.current = { quoteId, key: newIdempotencyKey("otc") };
    }
    const res = await OtcService.acceptQuote(quoteId, acceptKeyRef.current.key);
    setSubmitting(false);
    if (!res?.success) {
      if (res?.code === "KYC_REQUIRED" || res?.code === "KYB_REQUIRED") {
        alertErrorMessage(apiMessage(res, "Submit your KYC before accepting a quote."));
        openKycVerification();
        return;
      }
      alertErrorMessage(apiMessage(res, "Could not accept quote."));
      return;
    }
    acceptKeyRef.current = { quoteId: "", key: "" };
    const data = res.data || {};
    if (data.quote) setQuote(data.quote);
    if (data.trade?.id) {
      setTrade(data.trade);
      setTrades((prev) => {
        const id = String(data.trade.id);
        const rest = (Array.isArray(prev) ? prev : []).filter((row) => String(row.id) !== id);
        return [data.trade, ...rest];
      });
    }
    setRfq((prev) => (prev ? { ...prev, status: "ACCEPTED" } : prev));
    alertSuccessMessage("Quote accepted.");
    const tradesRes = await OtcService.getTrades({ page: 1, limit: 20 });
    const items = tradesRes?.success ? tradesRes.data?.items : null;
    if (Array.isArray(items)) setTrades(items);
    await refreshSpotBalances();
  }, [kycPending, kycVerified, quote, pairs, minNotional, minBaseByAsset, refreshSpotBalances]);

  useEffect(() => {
    refreshSpotBalances();
  }, [refreshSpotBalances]);

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const openWalletTrade = (row) => {
      const status = String(row?.status || "").toUpperCase();
      return status === "PENDING_SETTLEMENT" || status === "HELD";
    };
    const watching = openWalletTrade(trade) || trades.some(openWalletTrade);
    if (!watching) return undefined;
    let stopped = false;
    const timer = setInterval(async () => {
      const res = await OtcService.getTrades({ page: 1, limit: 20 });
      if (stopped || !res?.success || !Array.isArray(res.data?.items)) return;
      const items = res.data.items;
      setTrades(items);
      const current = trade?.id
        ? items.find((row) => String(row.id) === String(trade.id))
        : null;
      if (current) setTrade(current);
      await refreshSpotBalances();
    }, 5000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [isLoggedIn, trade, trades, refreshSpotBalances]);

  const lastN = Number(priceForSide(ticker, quoteType) || 0);
  const userPairLimit = selectedPair && selectedPair.limit_source === "user";
  const baseCode = pairBase;
  const quoteCode = pairQuote;
  const amountInQuote = amountAsset === quoteCode;
  const pairMinCoin = String((selectedPair && selectedPair.min_base) || "").trim();
  const configuredMinBase = pairMinCoin || (userPairLimit ? "" : String(minBaseByAsset[baseCode] || "").trim());
  const resolvedMinNotional = String(
    (selectedPair && selectedPair.min_notional) || (userPairLimit ? "" : minNotional) || ""
  ).trim();
  const minAmountRaw = amountUnitLimit({
    enteredInQuote: amountInQuote,
    baseRaw: configuredMinBase,
    quoteRaw: resolvedMinNotional,
    price: lastN,
    quoteCode,
    pick: "min",
  });
  const minBase = minAmountRaw
    ? `Min ${amountInQuote ? formatMoney(minAmountRaw) : minAmountRaw}`
    : "";
  const typedAmount = Number(amount);
  const amountApprox = lastN > 0 && Number.isFinite(typedAmount) && typedAmount > 0
    ? (amountInQuote
      ? `≈${formatApprox(typedAmount / lastN)} ${baseCode}`
      : `≈${formatApprox(typedAmount * lastN)} ${quoteCode}`)
    : "";
  const minQuoteLabel = resolvedMinNotional
    ? `Minimum ${formatMoney(resolvedMinNotional)} ${quoteCode}`
    : "";
  const affordableMax = maxAffordableAmount({
    quoteType,
    spendBalance,
    amountAsset,
    baseCode,
    quoteCode,
    last: priceForSide(ticker, quoteType),
  });

  const pairOptions = useMemo(
    () =>
      pairs.map((row) => {
        const base = coinCode(row.base);
        const quote = coinCode(row.quote);
        const asset = assets.find((item) => item.code === base);
        return {
          symbol: `${base}/${quote}`,
          name: asset ? asset.name : row.name || base,
          iconPath: asset ? asset.icon_path : row.iconPath || "",
        };
      }),
    [pairs, assets]
  );

  const baseOptions = useMemo(() => {
    const codes = [pairBase, pairQuote].filter(Boolean);
    return codes.map((code) => {
      const asset = assets.find((item) => item.code === code);
      return {
        symbol: code,
        name: asset ? asset.name : code,
        iconPath: asset ? asset.icon_path : "",
      };
    });
  }, [assets, pairBase, pairQuote]);

  const selectActiveRequest = useCallback((row, nextQuote) => {
    if (!row?.id) return;
    const key = rfqPairKey(row);
    if (!key) return;
    const side = String(row.side || "").toUpperCase() === "SELL" ? "sell" : "buy";
    const savedQuote = liveByPairRef.current[key]?.quote;
    const keptQuote = nextQuote?.id ? nextQuote : (savedQuote?.id ? savedQuote : null);
    setLiveByPair((prev) => ({
      ...prev,
      [key]: { rfq: row, quote: keptQuote },
    }));
    setRfq(row);
    setQuote(keptQuote);
    setQuoteType(side);
    passedPairRef.current = "";
    setPairKey(key);
    maxLockRef.current = false;
    setAmount("");
  }, []);

  const onPairChange = useCallback((key) => {
    passedPairRef.current = "";
    setPairKey(String(key || "").toUpperCase());
    maxLockRef.current = false;
    setAmount("");
  }, []);

  const setAmountFromUser = useCallback((value) => {
    maxLockRef.current = false;
    setAmount(sanitizeAmount(value, quotePlaces(amountAsset)));
  }, [amountAsset]);

  const onMax = useCallback(() => {
    if (!affordableMax) return;
    maxLockRef.current = true;
    setAmount(affordableMax);
  }, [affordableMax]);

  useEffect(() => {
    const scope = `${quoteType}|${baseCode}|${quoteCode}|${amountAsset}`;
    if (maxScopeRef.current && maxScopeRef.current !== scope) maxLockRef.current = false;
    maxScopeRef.current = scope;
    if (!maxLockRef.current || !affordableMax || affordableMax === amount) return;
    setAmount(affordableMax);
  }, [affordableMax, amount, quoteType, baseCode, quoteCode, amountAsset]);

  const amountReview = useMemo(() => {
    const last = deskHasLivePrice(ticker, payCoin, getCoin) ? priceForSide(ticker, quoteType) : null;
    const sized = sizeRfq({
      quoteType,
      amount,
      amountAsset,
      baseCode,
      quoteCode,
      last,
    });
    const userLimit = selectedPair && selectedPair.limit_source === "user";
    const pairMinCoin = String((selectedPair && selectedPair.min_base) || "").trim();
    return reviewAmount({
      amount,
      last,
      sized,
      baseCode,
      quoteCode,
      payCoin,
      minQtyRaw: pairMinCoin || (userLimit ? "" : minBaseByAsset[baseCode]),
      pairMin: String(
        (selectedPair && selectedPair.min_notional) || (userLimit ? "" : minNotional) || ""
      ).trim(),
      pairMax: String(
        (selectedPair && selectedPair.max_notional) || (userLimit ? "" : maxNotional) || ""
      ).trim(),
      pairMaxCoin: String((selectedPair && selectedPair.max_base) || "").trim(),
      spendBalance,
      enteredInQuote: amountAsset === quoteCode,
    });
  }, [
    ticker,
    quoteType,
    amount,
    amountAsset,
    baseCode,
    quoteCode,
    payCoin,
    getCoin,
    selectedPair,
    minBaseByAsset,
    minNotional,
    maxNotional,
    spendBalance,
  ]);

  const setQuoteTypeFromUser = useCallback((next) => {
    const side = String(next || "").toLowerCase() === "sell" ? "sell" : "buy";
    if (side === quoteType) return;
    maxLockRef.current = false;
    setAmount("");
    setQuoteType(side);
  }, [quoteType]);

  const startNewRequest = useCallback(() => {
    if (rfqRef.current?.id) dismissedRfqId.current = rfqRef.current.id;
    setRfq(null);
    setQuote(null);
    setTrade(null);
    maxLockRef.current = false;
    setAmount("");
  }, []);

  return {
    assetsLoading,
    quoteType,
    setQuoteType: setQuoteTypeFromUser,
    buyOpen,
    sellOpen,
    pairKey,
    onPairChange,
    selectActiveRequest,
    pairOptions,
    spotPairs: Array.isArray(marketData?.pairs) ? marketData.pairs : [],
    baseOptions,
    amountAsset,
    setAmountCoin,
    amount,
    setAmount: setAmountFromUser,
    onMax,
    minBase,
    amountApprox,
    minQuoteLabel,
    maxBase: affordableMax,
    amountError: amountReview.error,
    amountWarning: amountReview.warning,
    acceptLimitError,
    available: spendBalance,
    availableAsset: spendAsset,
    lockedBalance,
    getBalance,
    selectedPair,
    ticker,
    submitting,
    deskOpen,
    view,
    requestWindowOpen,
    requestWindowMessage,
    liveRequests,
    countdownMs,
    requestLeftMs,
    isLoggedIn,
    kycPending,
    kycVerified,
    kycCtaLabel,
    openKyc: openKycVerification,
    loginHref: "/login",
    rfq,
    quote,
    trade,
    trades,
    tradesLoading,
    submitRfq,
    refreshSpotBalances,
    landingBalanceOk,
    cancelRfq,
    acceptQuote,
    startNewRequest,
  };
}
